import { theme } from 'antd';
import { entityType } from 'config/entityType';
import { groupPermissionsBySourceForDevice } from 'library/helpers/accessControl';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectPermissionsByDeviceID } from 'reduxStore/permissions/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { Loader } from 'zComponents/atoms';
import PersonDrawer from 'zComponents/molecules/PersonDrawer';
import TeamDrawer from 'zComponents/molecules/TeamDrawer';
import { Empty, Flex, Tag, Typography } from 'zui/Antd';

const { Text } = Typography;

const AccessPermissionsPanelContent = ({ deviceID }) => {
	const {
		token: { fontWeightStrong, paddingSM, paddingXXS }
	} = theme.useToken();

	// ✅ Redux selectors for data hydration
	const permissions = useSelector((state) => selectPermissionsByDeviceID(state, deviceID));
	const people = useSelector(selectPeople);
	const teams = useSelector(selectTeams);
	const deviceGroups = useSelector(selectAssetGroups);
	const { loadingAddPermission, loadingDeletePermission } = useSelector((state) => state.Permissions);

	// ✅ Local state for entity drill-down
	const [openPersonDrawer, setOpenPersonDrawer] = useState(false);
	const [selectedPersonID, setSelectedPersonID] = useState(null);
	const [openTeamDrawer, setOpenTeamDrawer] = useState(false);
	const [selectedTeamID, setSelectedTeamID] = useState(null);

	// ✅ Process items using Source Distinction to handle redundant access paths (permissions only)
	const displayItems = useMemo(() => {
		return groupPermissionsBySourceForDevice({
			permissions,
			people,
			teams,
			deviceGroups
		});
	}, [permissions, people, teams, deviceGroups]);

	// ✅ Interaction Handlers
	const handleTagClick = (item) => {
		if (item.entityTypeID === entityType.PERSON) {
			setSelectedPersonID(item.personID);
			setOpenPersonDrawer(true);
		} else if (item.entityTypeID === entityType.TEAM) {
			setSelectedTeamID(item.teamID);
			setOpenTeamDrawer(true);
		}
	};

	return (
		<>
			<Flex wrap gap={paddingSM} style={{ width: '100%' }}>
				{loadingAddPermission || loadingDeletePermission ? (
					<Flex vertical align='center' gap={paddingSM} style={{ width: '100%', padding: paddingSM }}>
						<Loader />
						<Text type='secondary' style={{ fontSize: 12, textAlign: 'center' }}>
							Propagating permissions to devices — this may take a moment.
							<br />
							We'll notify you when it's done.
						</Text>
					</Flex>
				) : displayItems.length === 0 ? (
					<div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
						<Empty description='No access permissions' image={Empty.PRESENTED_IMAGE_SIMPLE} />
					</div>
				) : (
					displayItems.map((item) => (
						<Tag
							key={`${item.permissionID}-${item.entityID}`}
							color={item.badgeColor}
							style={{
								fontWeight: fontWeightStrong,
								borderColor: 'currentColor',
								cursor: 'pointer',
								marginInlineEnd: 0
							}}
							onClick={() => handleTagClick(item)}
						>
							<Flex align='center' gap={paddingXXS}>
								{item.Icon && <item.Icon color='currentColor' />}
								{item.label}
							</Flex>
						</Tag>
					))
				)}
			</Flex>

			{/* ✅ Entity Drill-down Drawers */}
			<PersonDrawer personID={selectedPersonID} visible={openPersonDrawer} setVisible={setOpenPersonDrawer} />
			<TeamDrawer teamID={selectedTeamID} visible={openTeamDrawer} setVisible={setOpenTeamDrawer} />
		</>
	);
};

export default AccessPermissionsPanelContent;
